import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, ErrorState, Loading, Metric, MetricRow, StatusPill, Table, fmtMinutes } from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * The complaint register — every fault the fleet has raised, filterable and drillable.
 *
 * Read-only on purpose. Acknowledging and resolving happen in the bay on the Android app, where the engineer
 * is standing at the machine with the evidence in front of them; a desk button that closes a job somebody else
 * is holding a spanner over is a coordination problem, not a feature. The console's job is to see and decide.
 *
 * Filters come through the URL so a dashboard tile can link straight here — `?filter=open-breakdown` — and so a
 * supervisor can send a colleague the exact view they are looking at.
 */
export default function Complaints() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const filter = params.get('filter') || 'all'
  const [q, setQ] = useState('')

  const list = useApi('/complaints')

  const rows = useMemo(() => {
    let r = list.data || []
    if (filter === 'open-breakdown') r = r.filter((c) => OPEN.has(c.status) && (c.priority === 'BREAKDOWN' || c.priority === 'SAFETY'))
    else if (filter === 'open') r = r.filter((c) => OPEN.has(c.status))
    else if (filter === 'safety') r = r.filter((c) => c.priority === 'SAFETY')
    else if (filter === 'unclassified') r = r.filter((c) => c.resolvedAt && !c.failureCodeId)
    if (q.trim()) {
      const t = q.trim().toLowerCase()
      r = r.filter((c) => [c.assetCode, c.assetName, c.descriptionText, c.department, c.rootCause]
        .some((v) => v && String(v).toLowerCase().includes(t)))
    }
    // Newest first: a register is read from the top.
    return [...r].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  }, [list.data, filter, q])

  const all = list.data || []
  const open = all.filter((c) => OPEN.has(c.status))

  return (
    <>
      <PageHeader
        title="Complaints"
        subtitle="Read-only. Acknowledging and resolving happen on the app, at the crane."
        actions={
          <input
            aria-label="Search complaints"
            placeholder="Search crane, description, cause…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ minWidth: 260 }}
          />
        }
      />

      {list.loading && <Loading rows={8} />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}

      {list.data && (
        <>
          <MetricRow>
            <Metric label="Total" value={all.length} onClick={() => setParams({})} />
            <Metric label="Open" value={open.length} kind={open.length ? 'warning' : 'success'} onClick={() => setParams({ filter: 'open' })} />
            <Metric
              label="Open breakdowns"
              value={open.filter((c) => c.priority === 'BREAKDOWN' || c.priority === 'SAFETY').length}
              kind="danger"
              onClick={() => setParams({ filter: 'open-breakdown' })}
            />
            <Metric
              label="Safety priority"
              value={all.filter((c) => c.priority === 'SAFETY').length}
              kind="safety"
              onClick={() => setParams({ filter: 'safety' })}
            />
            <Metric
              label="Resolved, no cause"
              value={all.filter((c) => c.resolvedAt && !c.failureCodeId).length}
              hint="Limits root-cause analysis"
              onClick={() => setParams({ filter: 'unclassified' })}
            />
          </MetricRow>

          {filter !== 'all' && (
            <p className="muted" style={{ margin: 'var(--md) 0' }}>
              Showing <strong>{FILTER_LABEL[filter] || filter}</strong> · {rows.length} of {all.length}
              {' '}<button className="btn" onClick={() => setParams({})}>Clear filter</button>
            </p>
          )}

          <Card>
            <Table
              keyOf={(c) => c.id}
              onRowClick={(c) => c.assetId && nav(`/cranes/${c.assetId}`)}
              columns={[
                {
                  key: 'crane',
                  header: 'Crane',
                  render: (c) => (
                    <span className="cell-stack">
                      <strong>{c.assetCode || '—'}</strong>
                      <span className="muted">{[c.department, c.bay || c.area].filter(Boolean).join(' · ')}</span>
                    </span>
                  ),
                },
                { key: 'priority', header: 'Priority', render: (c) => <StatusPill status={c.priority} /> },
                { key: 'status', header: 'Status', render: (c) => <StatusPill status={c.status} /> },
                {
                  key: 'what',
                  header: 'Reported',
                  render: (c) => (
                    <span className="cell-clamp" title={c.descriptionText || c.voiceTranscript || ''}>
                      {c.descriptionText || c.voiceTranscript || <span className="muted">No description</span>}
                    </span>
                  ),
                },
                { key: 'discipline', header: 'Discipline', render: (c) => title(c.discipline) },
                {
                  key: 'cause',
                  header: 'Root cause',
                  render: (c) => (c.rootCause
                    ? <span className="cell-clamp" title={c.rootCause}>{c.rootCause}</span>
                    : <span className="muted">{c.resolvedAt ? 'Not recorded' : '—'}</span>),
                },
                {
                  key: 'age',
                  header: 'Age',
                  numeric: true,
                  render: (c) => (c.createdAt ? fmtMinutes(minutesSince(c.createdAt)) : '—'),
                },
                {
                  key: 'mttr',
                  header: 'Repair time',
                  numeric: true,
                  render: (c) => (c.resolvedAt && c.createdAt
                    ? fmtMinutes(Math.max(0, minutesBetween(c.createdAt, c.resolvedAt) - (c.onHoldAccumMinutes || 0)))
                    : <span className="muted">—</span>),
                },
              ]}
              rows={rows}
              empty={<p className="muted">No complaints match this view.</p>}
            />
          </Card>
        </>
      )}
    </>
  )
}

const OPEN = new Set(['NEW', 'ASSIGNED', 'ACKNOWLEDGED', 'IN_PROGRESS', 'ON_HOLD', 'RESOLVED', 'REOPENED'])

const FILTER_LABEL = {
  open: 'open complaints',
  'open-breakdown': 'open breakdowns and safety faults',
  safety: 'safety-priority reports',
  unclassified: 'resolved without a failure code',
}

const title = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : '—')
const minutesSince = (iso) => (Date.now() - new Date(iso).getTime()) / 60000
const minutesBetween = (a, b) => (new Date(b).getTime() - new Date(a).getTime()) / 60000
